/**
 * CORE-BR — Weather Operational Intelligence · board advisory
 *
 * Shows the shop's weather policy state where people are actively placing
 * and managing work, and marks the outdoor bays it applies to.
 *
 * It is a notice, not a control. There is no button here that moves,
 * cancels or reassigns anything, and the bays it annotates remain exactly as
 * selectable as they were — that is the constitutional line this capability
 * sits on. The system may say a shop's own threshold has been crossed and
 * show the numbers; the person still decides.
 *
 * It also renders when weather could NOT be checked, which matters more than
 * it looks: without that, the absence of a warning means both "checked and
 * fine" and "the provider was down", and a reader has no way to tell which.
 */

import { AlertTriangle, CloudOff, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FRESHNESS_LABEL,
  isForecastAssessment,
  type BayWeatherAdvisory,
  type OutdoorWorkAssessment,
} from '@/lib/weather/outdoorAdvisory';

export interface OutdoorWeatherAdvisoryProps {
  assessment: OutdoorWorkAssessment;
  /** Advisories for the bays on this surface. Display only. */
  advisories?: BayWeatherAdvisory[];
  className?: string;
}

/**
 * The shop-level notice.
 *
 * Renders nothing when the policy is off or conditions are within it —
 * weather that is not operationally relevant should not occupy space on a
 * board people are trying to work from.
 */
export function OutdoorWeatherAdvisory({
  assessment,
  advisories,
  className,
}: OutdoorWeatherAdvisoryProps) {
  if (assessment.state === 'disabled' || assessment.state === 'clear') {
    return null;
  }

  const affected = (advisories ?? []).filter((a) => a.level === 'advisory');

  // A reading that has stopped being current is shown as unconfirmed, not
  // as today's verdict. It keeps the same neutral treatment as a failed
  // read, because in both cases the honest statement is the same: we cannot
  // tell you what it is like out there right now.
  if (assessment.state === 'unavailable' || assessment.state === 'stale') {
    return (
      <div
        data-testid="outdoor-weather-advisory"
        data-state={assessment.state}
        role="status"
        className={cn(
          'flex items-start gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 p-2 text-xs text-muted-foreground',
          className,
        )}
      >
        <CloudOff className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <p>{assessment.explanation}</p>
      </div>
    );
  }

  const isForecast = isForecastAssessment(assessment);

  return (
    <div
      data-testid="outdoor-weather-advisory"
      data-state="policy_conflict"
      data-reason={assessment.reason}
      data-freshness={assessment.freshness}
      role="status"
      className={cn(
        'rounded-md border border-warning/50 bg-warning/10 p-2 text-xs',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className="h-4 w-4 mt-0.5 shrink-0 text-warning"
          aria-hidden="true"
        />
        <div className="space-y-1">
          <p className="font-medium">
            {/*
              The label states which kind of reading this is before it states
              anything else. A forecast that reads like an observation is the
              one presentation error this capability is expressly forbidden
              from making.
            */}
            {isForecast
              ? 'Forecast conflicts with this shop’s outdoor-work policy'
              : 'Outdoor work conflicts with this shop’s weather policy'}
          </p>
          <p data-testid="outdoor-weather-advisory-explanation">
            {assessment.explanation}
          </p>

          <p className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span data-testid="outdoor-weather-advisory-freshness">
              {isForecast
                ? `Forecast for ${assessment.evidence.effective_at}`
                : FRESHNESS_LABEL[assessment.freshness]}
            </span>
            <span aria-hidden="true">·</span>
            <span>Source: {assessment.evidence.source}</span>
          </p>

          {affected.length > 0 && (
            <p
              data-testid="outdoor-weather-advisory-bays"
              className="text-muted-foreground"
            >
              Applies to:{' '}
              {affected.map((a) => a.bayName || a.bayId).join(', ')}
            </p>
          )}

          {/*
            Stated outright. A person reading a warning is entitled to know
            whether it is advice or a decision that has already been made for
            them — and here it is always the former.
          */}
          <p className="text-muted-foreground italic">
            This is advisory. Outdoor bays remain available and you can
            proceed.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The per-bay marker, for use beside a bay on a floor or board surface.
 *
 * Returns null for bays with nothing to say, so indoor and undesignated
 * bays are untouched.
 */
export function BayWeatherMarker({
  advisory,
  className,
}: {
  advisory: BayWeatherAdvisory;
  className?: string;
}) {
  if (advisory.level === 'none') return null;

  const isConflict = advisory.level === 'advisory';
  return (
    <span
      data-testid={`bay-weather-marker-${advisory.bayId}`}
      data-level={advisory.level}
      title={advisory.message ?? undefined}
      className={cn(
        'inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px]',
        isConflict
          ? 'bg-warning/20 text-warning-foreground'
          : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {isConflict ? (
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      ) : (
        <CloudOff className="h-3 w-3" aria-hidden="true" />
      )}
      {isConflict ? 'Weather' : 'Unverified'}
    </span>
  );
}
